import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

// Browser and Vite messages for a lazily imported chunk that no longer exists on the
// server — the fingerprint of a tab that outlived a deploy. Nothing else produces them.
export const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Failed to load module script|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/;

export const RELOAD_WINDOW_MS = 10_000;
export const RELOAD_TIMESTAMP_KEY = 'lazyChunkReloadedAt';

export interface LazyReloadDeps {
  reload: () => void;
  now: () => number;
  storage: () => Storage;
}

const defaultDeps: LazyReloadDeps = {
  reload: () => window.location.reload(),
  now: () => Date.now(),
  storage: () => window.sessionStorage,
};

export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error && CHUNK_LOAD_ERROR_PATTERN.test(error.message);

/**
 * Record a reload attempt and report whether it may proceed.
 *
 * At most one reload per RELOAD_WINDOW_MS, so a chunk that is genuinely missing degrades
 * to the error screen instead of a reload loop. Without usable storage there is no guard,
 * so there is no reload either.
 */
export const claimReload = (deps: LazyReloadDeps = defaultDeps): boolean => {
  try {
    const storage = deps.storage();
    const now = deps.now();
    const last = Number(storage.getItem(RELOAD_TIMESTAMP_KEY));
    if (last && now - last < RELOAD_WINDOW_MS) {
      return false;
    }
    storage.setItem(RELOAD_TIMESTAMP_KEY, String(now));
    return true;
  } catch {
    return false;
  }
};

/**
 * React.lazy that reloads the page, at most once per RELOAD_WINDOW_MS, when the chunk
 * itself cannot be loaded. Every other error is re-thrown to the route's errorElement.
 *
 * The wrapped component must render under a Suspense boundary: while the reload is in
 * flight the factory returns a promise that never settles, which outside Suspense is a
 * render error rather than a pending state.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the constraint React.lazy itself uses
export const lazyWithReload = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  deps: LazyReloadDeps = defaultDeps
): LazyExoticComponent<T> =>
  lazy(() =>
    factory().catch((error: unknown) => {
      if (!isChunkLoadError(error) || !claimReload(deps)) {
        throw error;
      }
      deps.reload();
      return new Promise<{ default: T }>(() => {});
    })
  );
